import {Button} from '@dropbox/dig-components/buttons';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {OpenLine, SearchLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {
  KNOWLEDGE_SOURCES,
  knowledgeLinkState,
  logKnowledgeHomeItemClicked,
  logKnowledgeHomeSearched,
  readKnowledgeSource,
} from 'analytics/knowledgeAnalytics';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Breadcrumb} from 'components/DSYS/Breadcrumb';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {AI_APPLIED_HUB_URL} from 'components/layout/KnowledgeNavItem';
import {getKnowledgeCategoryPath, getKnowledgePagePath, ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useDeferredValue, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router';
import newsPostStyles from 'views/news/NewsPostRight.module.css';

import {ServiceNowKnowledgePageNode, ServiceNowNavCategory} from './api';
import {useAllKnowledgeCategoryPages, useKnowledgeNavMenu} from './hooks';
import {KnowledgeGuidesCard} from './KnowledgeGuidesCard';
import {matchesKnowledgeSearch} from './knowledgeSearch';
import {
  flattenTree,
  getCachedPagePreviewText,
  getExternalNodeKind,
  KNOWLEDGE_SEARCH_MAX_LENGTH,
  KnowledgeContentLoading,
  SubPageCardList,
} from './KnowledgeView';
import styles from './KnowledgeView.module.css';

const TOPICS_PER_CATEGORY_CARD = 5;
const ARTICLES_PER_CATEGORY_COLLAPSED = 6;
// Log a search once typing pauses, not on every keystroke.
const SEARCH_LOG_DEBOUNCE_MS = 1000;

const browseLinkState = knowledgeLinkState(KNOWLEDGE_SOURCES.home);
const searchLinkState = knowledgeLinkState(KNOWLEDGE_SOURCES.homeSearch);

type TopicEntry = {
  id: string;
  title: string;
  parentTitle: string | null;
  category: ServiceNowNavCategory;
};

const collectTopics = (categories: ServiceNowNavCategory[]): TopicEntry[] =>
  categories.flatMap((category) =>
    category.pages.flatMap((page) => [
      {id: page.id, title: page.title, parentTitle: null, category},
      ...page.children.map((child) => ({
        id: child.id,
        title: child.title,
        parentTitle: page.title,
        category,
      })),
    ])
  );

const isSearchableNode = (node: ServiceNowKnowledgePageNode): boolean =>
  node.node_type === 'article' || getExternalNodeKind(node) === 'request';

const CategoryCard = ({category}: {category: ServiceNowNavCategory}) => {
  const visibleTopics = category.pages.slice(0, TOPICS_PER_CATEGORY_CARD);
  const hiddenTopicCount = category.pages.length - visibleTopics.length;

  return (
    <Box
      borderColor="Border Base"
      borderStyle="Solid"
      borderWidth="1"
      borderRadius="Medium"
      padding="16"
    >
      {/* Stack spaces children with margins, which inline links ignore. */}
      <Stack gap="8">
        <Box>
          <Link
            to={getKnowledgeCategoryPath(category.slug)}
            state={browseLinkState}
            monochromatic
            hasNoUnderline
            showUnderlineOnHover
            onClick={() =>
              logKnowledgeHomeItemClicked({
                itemType: 'category',
                categorySlug: category.slug,
                mode: 'browse',
              })
            }
          >
            <Text isBold>{category.name}</Text>
          </Link>
        </Box>
        {visibleTopics.map((topic, index) => (
          <Box key={topic.id}>
            <Link
              to={getKnowledgePagePath(category.slug, topic.id)}
              state={browseLinkState}
              monochromatic
              hasNoUnderline
              showUnderlineOnHover
              onClick={() =>
                logKnowledgeHomeItemClicked({
                  itemType: 'topic',
                  categorySlug: category.slug,
                  mode: 'browse',
                  rank: index + 1,
                })
              }
            >
              <Text color="subtle">{topic.title}</Text>
            </Link>
          </Box>
        ))}
        {hiddenTopicCount > 0 ? (
          <Box>
            <Link
              to={getKnowledgeCategoryPath(category.slug)}
              state={browseLinkState}
              hasNoUnderline
              showUnderlineOnHover
              onClick={() =>
                logKnowledgeHomeItemClicked({
                  itemType: 'more_topics',
                  categorySlug: category.slug,
                  mode: 'browse',
                })
              }
            >
              <Text size="small">{t('show_more_count', {count: hiddenTopicCount})}</Text>
            </Link>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

const AiAppliedHubCard = () => (
  <Box
    borderColor="Border Base"
    borderStyle="Solid"
    borderWidth="1"
    borderRadius="Medium"
    padding="16"
  >
    <Link
      to={AI_APPLIED_HUB_URL}
      openInNewTab
      rel="noopener noreferrer"
      monochromatic
      hasNoUnderline
      showUnderlineOnHover
      aria-label={`${t('ai_applied_hub')}, ${t('knowledge_opens_in_new_window')}`}
      onClick={() => logKnowledgeHomeItemClicked({itemType: 'ai-applied-hub', mode: 'browse'})}
    >
      <Box display="flex" alignItems="center" style={{gap: 4}}>
        <Text isBold>{t('ai_applied_hub')}</Text>
        <UIIcon src={OpenLine} size="small" isOpticallyAligned />
      </Box>
    </Link>
  </Box>
);

const ArticleGroup = ({
  category,
  pages,
  groupRank,
  queryLength,
}: {
  category: ServiceNowNavCategory;
  pages: ServiceNowKnowledgePageNode[];
  groupRank: number;
  queryLength: number;
}) => {
  const [showAll, setShowAll] = useState(false);
  const visiblePages = showAll ? pages : pages.slice(0, ARTICLES_PER_CATEGORY_COLLAPSED);

  return (
    <div className={styles.sectionBlock}>
      <Link
        to={getKnowledgeCategoryPath(category.slug)}
        state={searchLinkState}
        monochromatic
        hasNoUnderline
        showUnderlineOnHover
        onClick={() =>
          logKnowledgeHomeItemClicked({
            itemType: 'category',
            categorySlug: category.slug,
            mode: 'search',
            queryLength,
            groupRank,
          })
        }
      >
        <Text color="subtle">{category.name}</Text>
      </Link>
      <Box paddingTop="12">
        <SubPageCardList
          pages={visiblePages}
          categorySlug={category.slug}
          linkSource={KNOWLEDGE_SOURCES.homeSearch}
          onPageClick={(page, index) =>
            logKnowledgeHomeItemClicked({
              itemType: getExternalNodeKind(page) === 'request' ? 'request' : 'article',
              categorySlug: category.slug,
              mode: 'search',
              queryLength,
              rank: index + 1,
              groupRank,
            })
          }
        />
      </Box>
      {pages.length > ARTICLES_PER_CATEGORY_COLLAPSED ? (
        <Box paddingTop="12">
          <Button
            variant="transparent"
            size="small"
            onClick={() => setShowAll((current) => !current)}
          >
            {showAll
              ? t('show_less')
              : t('show_more_count', {count: pages.length - ARTICLES_PER_CATEGORY_COLLAPSED})}
          </Button>
        </Box>
      ) : null}
    </div>
  );
};

const KnowledgeHomeSearchResults = ({
  query,
  categories,
  cacheScope,
}: {
  query: string;
  categories: ServiceNowNavCategory[];
  cacheScope?: string | null;
}) => {
  const categorySlugs = useMemo(() => categories.map((category) => category.slug), [categories]);
  // Article lists carry bodies, so only pull every category once someone searches.
  const {pagesByCategory, isLoading} = useAllKnowledgeCategoryPages(categorySlugs, {
    enabled: true,
    cacheScope,
  });
  const topics = useMemo(() => collectTopics(categories), [categories]);
  const matchedTopics = useMemo(
    () => topics.filter((topic) => matchesKnowledgeSearch(query, [topic.title])),
    [query, topics]
  );
  const articleGroups = useMemo(() => {
    const knownSlugs = new Set(categorySlugs);
    const seenIds = new Set<string>();
    const pagesBySlug = new Map<string, ServiceNowKnowledgePageNode[]>();

    categories.forEach((category, index) => {
      for (const node of flattenTree(pagesByCategory[index] ?? [])) {
        if (seenIds.has(node.id) || !isSearchableNode(node)) {
          continue;
        }
        seenIds.add(node.id);
        if (
          !matchesKnowledgeSearch(query, [
            node.title,
            getCachedPagePreviewText(node).slice(0, 1000),
          ])
        ) {
          continue;
        }
        const slug =
          node.primary_category_slug && knownSlugs.has(node.primary_category_slug)
            ? node.primary_category_slug
            : category.slug;
        pagesBySlug.set(slug, [...(pagesBySlug.get(slug) ?? []), node]);
      }
    });

    return categories
      .filter((category) => pagesBySlug.has(category.slug))
      .map((category) => ({category, pages: pagesBySlug.get(category.slug) ?? []}));
  }, [categories, categorySlugs, pagesByCategory, query]);

  const articleResultCount = articleGroups.reduce((count, group) => count + group.pages.length, 0);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const timer = setTimeout(
      () =>
        logKnowledgeHomeSearched({
          queryLength: query.length,
          topicResults: matchedTopics.length,
          articleResults: articleResultCount,
          categoriesWithResults: articleGroups.length,
        }),
      SEARCH_LOG_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [articleGroups.length, articleResultCount, isLoading, matchedTopics.length, query]);

  if (!isLoading && matchedTopics.length === 0 && articleGroups.length === 0) {
    return (
      <Box paddingY="24" textAlign="center" role="status">
        <Text color="subtle">{t('knowledge_home_search_no_results', {query})}</Text>
      </Box>
    );
  }

  return (
    <>
      {matchedTopics.length > 0 ? (
        <div className={styles.sectionBlock}>
          <Text isBold>{t('knowledge_topics')}</Text>
          <Box paddingTop="20">
            <div className={styles.subPagesGrid}>
              {matchedTopics.map((topic, index) => (
                <Link
                  key={topic.id}
                  className={styles.subPageCard}
                  to={getKnowledgePagePath(topic.category.slug, topic.id)}
                  state={searchLinkState}
                  monochromatic
                  hasNoUnderline
                  onClick={() =>
                    logKnowledgeHomeItemClicked({
                      itemType: 'topic',
                      categorySlug: topic.category.slug,
                      mode: 'search',
                      queryLength: query.length,
                      rank: index + 1,
                    })
                  }
                >
                  <Text isBold>{topic.title}</Text>
                  <Text color="subtle" size="small">
                    {topic.parentTitle
                      ? t('knowledge_topic_location', {
                          category: topic.category.name,
                          topic: topic.parentTitle,
                        })
                      : topic.category.name}
                  </Text>
                </Link>
              ))}
            </div>
          </Box>
        </div>
      ) : null}
      {articleGroups.length > 0 ? (
        <div className={styles.sectionBlock}>
          <Text isBold>{t('knowledge_articles')}</Text>
          {articleGroups.map(({category, pages}, groupIndex) => (
            <ArticleGroup
              key={category.slug}
              category={category}
              pages={pages}
              groupRank={groupIndex + 1}
              queryLength={query.length}
            />
          ))}
        </div>
      ) : null}
      {isLoading ? (
        <Box paddingY="24" textAlign="center" role="status">
          <Text color="subtle">{t('knowledge_home_searching')}</Text>
        </Box>
      ) : null}
    </>
  );
};

export const KnowledgeHome = () => {
  const {employee: currentEmployee} = useAtomValue(loggedInEmployeeAtom);
  const cacheScope = currentEmployee?.ldap;
  const navMenuQuery = useKnowledgeNavMenu({cacheScope});
  const categories = useMemo(
    () => navMenuQuery.data?.categories ?? [],
    [navMenuQuery.data?.categories]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery.trim());
  const location = useLocation();

  useDocumentTitle(t('knowledge'));

  useEffect(() => {
    analyticsLogger().logEvent('KNOWLEDGE_HOME_VIEWED', {
      source: readKnowledgeSource(location.state),
    });
    // Log once per visit, with the source it was opened from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  if (navMenuQuery.isLoading) {
    return (
      <Layout.Container>
        <div className={styles.knowledgeBody}>
          <KnowledgeContentLoading />
        </div>
      </Layout.Container>
    );
  }

  return (
    <Layout.Container>
      <div className={styles.knowledgeBody}>
        <div className={newsPostStyles.newsPostRight}>
          <div
            className={styles.breadcrumbActionsRow}
            role="navigation"
            aria-label={t('knowledge_breadcrumb_nav')}
          >
            <Breadcrumb
              path={[{to: ROUTE_PATHS.KNOWLEDGE, children: t('knowledge')}]}
              withMarginBottom={false}
            />
          </div>
          <div className={styles.pageTitleRow}>
            <Title
              className={`${newsPostStyles.title} ${styles.pageTitle}`}
              weightVariant="emphasized"
              size="large"
            >
              {t('knowledge')}
            </Title>
          </div>
          {categories.length === 0 ? (
            <Box paddingY="24" role="status">
              <Text color="subtle">{t('knowledge_home_empty')}</Text>
            </Box>
          ) : (
            <>
              <Box paddingTop="16">
                <TextInput
                  id="knowledge-home-search"
                  type="search"
                  value={searchQuery}
                  maxLength={KNOWLEDGE_SEARCH_MAX_LENGTH}
                  placeholder={t('knowledge_home_search_placeholder')}
                  aria-label={t('knowledge_home_search_placeholder')}
                  withLeftAccessory={<UIIcon src={SearchLine} role="presentation" />}
                  onChange={(event) => setSearchQuery(event.currentTarget.value)}
                />
              </Box>
              {deferredSearchQuery ? (
                <KnowledgeHomeSearchResults
                  query={deferredSearchQuery}
                  categories={categories}
                  cacheScope={cacheScope}
                />
              ) : (
                <div className={styles.sectionBlock}>
                  <div className={styles.subPagesGrid}>
                    {categories.map((category) => (
                      <CategoryCard key={category.id} category={category} />
                    ))}
                    <AiAppliedHubCard />
                    <KnowledgeGuidesCard />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout.Container>
  );
};
